import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { getImageUrl } from "@/lib/images";
import { ArrowLeft, Upload, X, Flame, Plus, Check, ChevronLeft, ChevronRight, MapPin, Loader2 } from "lucide-react";
import { AmenityIcon } from "@/components/AmenityIcon";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import MapPicker from "@/components/MapPicker";
import {
  InputField, TextInput, SelectInput, TextArea,
  SegmentedToggle, PaymentMethodRow,
  WeeklyDayRow, ReviewRow, PeakHourGrid, ExtraItemEditor,
} from "@/components/CourtFormFields";

const SPORTS = ["football", "basketball", "tennis", "padel", "pickleball"];

const COURT_TYPES_BY_SPORT = {
  football: [{ value: "5-a-side", labelKey: "owner.court_form.court_type_5_a_side" }, { value: "6-a-side", labelKey: "owner.court_form.court_type_6_a_side" }, { value: "7-a-side", labelKey: "owner.court_form.court_type_7_a_side" }, { value: "full", labelKey: "owner.court_form.court_type_full" }],
  basketball: [{ value: "half", labelKey: "owner.court_form.court_type_half" }, { value: "full", labelKey: "owner.court_form.court_type_full" }],
  tennis: [{ value: "mini", labelKey: "owner.court_form.court_type_mini" }, { value: "standard", labelKey: "owner.court_form.court_type_standard" }, { value: "full", labelKey: "owner.court_form.court_type_full" }],
  padel: [{ value: "standard", labelKey: "owner.court_form.court_type_standard" }],
  pickleball: [{ value: "standard", labelKey: "owner.court_form.court_type_standard" }],
};

const SURFACES_BY_SPORT = {
  football: [{ value: "grass", labelKey: "owner.court_form.surface_football_grass" }, { value: "artificial_grass", labelKey: "owner.court_form.surface_football_artificial_grass" }, { value: "hard", labelKey: "owner.court_form.surface_football_hard" }, { value: "parquet", labelKey: "owner.court_form.surface_football_parquet" }],
  basketball: [{ value: "parquet", labelKey: "owner.court_form.surface_basketball_parquet" }, { value: "hard", labelKey: "owner.court_form.surface_basketball_hard" }, { value: "artificial_grass", labelKey: "owner.court_form.surface_basketball_artificial_grass" }],
  tennis: [{ value: "hard", labelKey: "owner.court_form.surface_tennis_hard" }, { value: "clay", labelKey: "owner.court_form.surface_tennis_clay" }, { value: "grass", labelKey: "owner.court_form.surface_tennis_grass" }, { value: "artificial_grass", labelKey: "owner.court_form.surface_tennis_artificial_grass" }, { value: "carpet", labelKey: "owner.court_form.surface_tennis_carpet" }, { value: "parquet", labelKey: "owner.court_form.surface_tennis_parquet" }],
  padel: [{ value: "artificial_grass", labelKey: "owner.court_form.surface_padel_artificial_grass" }, { value: "hard", labelKey: "owner.court_form.surface_padel_hard" }, { value: "concrete", labelKey: "owner.court_form.surface_padel_concrete" }],
  pickleball: [{ value: "hard", labelKey: "owner.court_form.surface_pickleball_hard" }, { value: "parquet", labelKey: "owner.court_form.surface_pickleball_parquet" }, { value: "concrete", labelKey: "owner.court_form.surface_pickleball_concrete" }],
};

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const DEFAULT_COURT = {
  name: "", sport: "football", description: "", address: "", city: "",
  lat: 31.9454, lng: 35.9284, price_per_slot: 15, indoor: false,
  court_type: "", court_surface: "", amenities: [], images: [], rules: "",
  open_hour: 8, close_hour: 22, auto_accept: true, peak_hours: [],
  peak_multiplier: 0, weekend_multiplier: 0, slot_duration: 60,
  slot_durations: [30, 60, 90, 120], default_slot_duration: 60,
  slot_pricing: { "30": 10, "60": 15, "90": 22, "120": 28 },
  weekly_hours: [], currency: "JOD", country: "JO",
  accepted_payment_methods: ["online"], whatsapp_number: "", phone_number: "",
  google_maps_url: "",
  place_id: "",
  cliQ_alias: "",
  cliQ_enabled: false,
  contact_visibility: "confirmed_only",
  deposit_percent: 0,
  cancellation_hours: 24,
  cancellation_fee_percent: 10,
  cancellation_tiers: [],
  weather_cancellation: false,
  no_show_keep_deposit: false,
  facility_id: "",
};

const CANCELLATION_PRESETS = {
  none: [{ hours_before: 0, refund_percent: 0 }],
  flexible: [{ hours_before: 12, refund_percent: 100 }, { hours_before: 0, refund_percent: 50 }],
  standard: [{ hours_before: 48, refund_percent: 100 }, { hours_before: 24, refund_percent: 50 }, { hours_before: 0, refund_percent: 0 }],
  strict: [{ hours_before: 72, refund_percent: 100 }, { hours_before: 24, refund_percent: 25 }, { hours_before: 0, refund_percent: 0 }],
};

const STEPS = [
  { key: "basic", labelKey: "owner.court_form.wizard_basic" },
  { key: "location", labelKey: "owner.court_form.wizard_location" },
  { key: "media", labelKey: "owner.court_form.wizard_media" },
  { key: "pricing", labelKey: "owner.court_form.wizard_pricing" },
  { key: "scheduling", labelKey: "owner.court_form.wizard_scheduling" },
  { key: "review", labelKey: "owner.court_form.wizard_review" },
];

const PAYMENT_METHODS = [
  { key: "cod", labelKey: "owner.court_form.payment_cod_enabled", desc: "Player pays cash at the court upon arrival" },
];

const CITIES_BY_COUNTRY = {
  JO: ["amman", "zarqa", "irbid", "aqaba", "madaba", "salt", "jerash", "ajloun", "karak", "tafilah", "maan", "mafraq", "ramtha"],
};

const CITY_COORDS = {
  amman: { lat: 31.9454, lng: 35.9284 },
  zarqa: { lat: 32.0833, lng: 36.1000 },
  irbid: { lat: 32.5556, lng: 35.8500 },
  aqaba: { lat: 29.5267, lng: 35.0078 },
  madaba: { lat: 31.7167, lng: 35.8000 },
  salt: { lat: 32.0392, lng: 35.7272 },
  jerash: { lat: 32.2769, lng: 35.9053 },
  ajloun: { lat: 32.3333, lng: 35.7500 },
  karak: { lat: 31.1833, lng: 35.7000 },
  tafilah: { lat: 30.8333, lng: 35.6000 },
  maan: { lat: 30.1967, lng: 35.7333 },
  mafraq: { lat: 32.3500, lng: 36.2000 },
  ramtha: { lat: 32.5583, lng: 36.0167 },
};

const normalizePlaceText = (value) => String(value || "")
  .normalize("NFKD")
  .toLocaleLowerCase()
  .replace(/[\u064B-\u065F\u0670]/g, "")
  .replace(/[\u0622\u0623\u0625]/g, "\u0627")
  .replace(/\u0629/g, "\u0647")
  .replace(/[^a-z0-9\u0600-\u06ff]+/g, " ")
  .trim();

const DRAFT_KEY = "gocaptain_draft_court";

export default function AddCourt() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { courtId } = useParams();
  const isEditing = Boolean(courtId);

  const [f, setF] = useState({ ...DEFAULT_COURT });
  const [slotConfigs, setSlotConfigs] = useState({ "30": { enabled: true, price: 10 }, "60": { enabled: true, price: 15 }, "90": { enabled: true, price: 22 }, "120": { enabled: true, price: 28 } });
  const [defaultDuration, setDefaultDuration] = useState(60);
  const [step, setStep] = useState(0);
  const [imgInput, setImgInput] = useState("");
  const [mapsUrl, setMapsUrl] = useState("");
  const [mapsImporting, setMapsImporting] = useState(false);
  const [mapsImportError, setMapsImportError] = useState("");
  const [mapsImportResult, setMapsImportResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [allAmenities, setAllAmenities] = useState([]);
  const [touched, setTouched] = useState({});
  const [dragging, setDragging] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const fileRef = useRef(null);
  const formRef = useRef(f);

  useEffect(() => {
    formRef.current = f;
  }, [f]);

  useEffect(() => {
    api.get("/amenities").then((r) => setAllAmenities(r.data)).catch((e) => {
      if (import.meta.env.DEV) console.warn("amenities load failed:", e);
    });
    api.get("/owner/facilities").then((r) => setFacilities(r.data)).catch((e) => {
      if (import.meta.env.DEV) console.warn("facilities load failed:", e);
    });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    if (!isEditing) return;
    api.get(`/courts/${courtId}`).then((r) => {
      const data = r.data;
      const durations = data.slot_durations || [30, 60, 90, 120];
      const pricing = data.slot_pricing || {};
      const configs = {};
      [30, 60, 90, 120].forEach((d) => {
        configs[String(d)] = { enabled: durations.includes(d), price: pricing[String(d)] ?? Math.round((data.price_per_slot || 15) * (d / 60) * 10) / 10 };
      });
      setSlotConfigs(configs);
      setDefaultDuration(data.default_slot_duration || durations[0] || 60);
      const loaded = { ...DEFAULT_COURT, ...data };
      if (loaded.peak_multiplier != null && loaded.peak_multiplier >= 0.5) loaded.peak_multiplier = Math.round((loaded.peak_multiplier - 1) * 100);
      if (loaded.weekend_multiplier != null && loaded.weekend_multiplier >= 0.5) loaded.weekend_multiplier = Math.round((loaded.weekend_multiplier - 1) * 100);
      setF(loaded);
      setMapsUrl(loaded.google_maps_url || "");
      setLoading(false);
    }).catch(() => {
      toast.error("Court not found");
      navigate(-1);
    });
  }, [courtId, isEditing, navigate]);

  useEffect(() => {
    if (isEditing) return;
    const timer = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: f, slotConfigs, mapsUrl }));
    }, 1500);
    return () => clearTimeout(timer);
  }, [f, slotConfigs, mapsUrl, isEditing]);

  useEffect(() => {
    if (isEditing) return;
    try {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        if (parsed.form && parsed.form.name) {
          setShowDraftModal(true);
          return;
        }
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn("failed to restore court draft:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const set = (k, v) => setF({ ...f, [k]: v });

  const loadDraft = () => {
    try {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        setF({ ...DEFAULT_COURT, ...parsed.form });
        if (parsed.slotConfigs) setSlotConfigs(parsed.slotConfigs);
        setMapsUrl(parsed.mapsUrl || parsed.form?.google_maps_url || "");
      }
    } catch { /* ignore */ }
    setShowDraftModal(false);
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setShowDraftModal(false);
  };
  const setLatLng = (lat, lng) => setF({ ...f, lat, lng });
  const touch = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const fieldError = (field) => {
    if (!touched[field]) return false;
    if (field === "name") return !f.name.trim();
    if (field === "city") return !f.city.trim();
    if (field === "address") return !f.address.trim();
    return false;
  };

  const errorMsg = (field) => {
    if (field === "name") return t("owner.court_form.name_required");
    if (field === "city") return t("owner.court_form.city_required");
    if (field === "address") return t("owner.court_form.address_required");
    return "";
  };

  const resolveImportedCity = (value, countryCode) => {
    const normalized = normalizePlaceText(value);
    if (!normalized) return "";

    const alias = normalized === "tafileh" ? "tafilah" : normalized;
    const supportedCities = CITIES_BY_COUNTRY[countryCode] || CITIES_BY_COUNTRY.JO;
    return supportedCities.find((cityKey) => {
      const key = normalizePlaceText(cityKey);
      const translated = normalizePlaceText(t(`cities.${cityKey}`));
      return alias === key || alias.includes(key) || alias === translated || alias.includes(translated);
    }) || String(value).trim();
  };

  const importGoogleMapsPlace = async () => {
    const url = mapsUrl.trim();
    if (!url || mapsImporting) return;

    setMapsImporting(true);
    setMapsImportError("");
    setMapsImportResult(null);

    try {
      const language = (i18n.resolvedLanguage || i18n.language)?.startsWith("ar") ? "ar" : "en";
      const response = await api.post("/place-details", { url, language });
      const data = response.data;
      if (!data?.success) throw new Error(t("owner.court_form.maps_import_error"));

      const next = { ...formRef.current };
      const importedFields = new Set();
      let keptExisting = false;
      const addField = (field) => importedFields.add(field);
      const applyWhenEmpty = (field, value, resultField = field) => {
        const candidate = typeof value === "string" ? value.trim() : value;
        if (candidate === undefined || candidate === null || candidate === "") return;
        if (String(next[field] ?? "").trim() === "") {
          next[field] = candidate;
          addField(resultField);
        } else if (String(next[field]) !== String(candidate)) {
          keptExisting = true;
        }
      };

      applyWhenEmpty("name", data.name);
      applyWhenEmpty("address", data.address);

      const normalizedCountry = normalizePlaceText(data.country_code || data.country);
      const importedCountry = normalizedCountry === "jo" || normalizedCountry === "jordan" || normalizedCountry === "\u0627\u0644\u0627\u0631\u062f\u0646"
        ? "JO"
        : "";
      if (importedCountry && CITIES_BY_COUNTRY[importedCountry]) {
        if (!next.country) {
          next.country = importedCountry;
          addField("country");
        } else if (next.country !== importedCountry) {
          keptExisting = true;
        }
      }

      if (data.city) {
        applyWhenEmpty("city", resolveImportedCity(data.city, importedCountry || next.country || "JO"));
      }

      const hasCoordinates = data.lat !== undefined && data.lat !== null && data.lat !== ""
        && data.lng !== undefined && data.lng !== null && data.lng !== "";
      const lat = Number(data.lat);
      const lng = Number(data.lng);
      if (hasCoordinates && Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        next.lat = lat;
        next.lng = lng;
        addField("location");
      }

      if (data.phone) {
        applyWhenEmpty("phone_number", data.phone, "phone");
        applyWhenEmpty("whatsapp_number", data.phone, "whatsapp");
      }

      const openHour = Number(data.open_hour);
      const closeHour = Number(data.close_hour);
      const hasReliableHours = Number.isInteger(openHour) && Number.isInteger(closeHour)
        && openHour >= 0 && openHour <= 23 && closeHour >= 1 && closeHour <= 24;
      if (hasReliableHours) {
        if (next.open_hour === DEFAULT_COURT.open_hour && next.close_hour === DEFAULT_COURT.close_hour) {
          next.open_hour = openHour;
          next.close_hour = closeHour;
          addField("hours");
        } else if (next.open_hour !== openHour || next.close_hour !== closeHour) {
          keptExisting = true;
        }
      }

      const canonicalUrl = String(data.google_maps_url || data.canonical_url || url).trim();
      next.google_maps_url = canonicalUrl;
      if (data.place_id) next.place_id = data.place_id;
      addField("maps_link");

      setF(next);
      setMapsUrl(canonicalUrl);
      setMapsImportResult({ fields: [...importedFields], keptExisting });
      toast.success(t("owner.court_form.maps_import_success"));
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      let message = t("owner.court_form.maps_import_error");
      if (status === 400 || status === 422) {
        message = t("owner.court_form.maps_import_invalid_error");
      } else if (status === 502 || (status >= 500 && status <= 599)) {
        message = t("owner.court_form.maps_import_service_error");
      } else if (detail) {
        message = formatApiError(detail);
      }
      setMapsImportError(message);
      toast.error(message);
    } finally {
      setMapsImporting(false);
    }
  };

  const togglePeak = (h) => {
    const cur = f.peak_hours || [];
    set("peak_hours", cur.includes(h) ? cur.filter((x) => x !== h) : [...cur, h].sort((a, b) => a - b));
  };

  const setExtras = (next) => set("extras", next);

  const updateWeeklyDay = (day, updates) => {
    const current = f.weekly_hours || [];
    const idx = current.findIndex((wh) => wh.day === day);
    if (idx >= 0) {
      const next = [...current];
      next[idx] = { ...next[idx], day, ...updates };
      set("weekly_hours", next);
    } else {
      set("weekly_hours", [...current, { day, open_hour: f.open_hour, close_hour: f.close_hour, is_closed: false, ...updates }]);
    }
  };

  const applyToAllDays = () => {
    const source = (f.weekly_hours || []).find((wh) => !wh.is_closed);
    const openH = source?.open_hour ?? f.open_hour;
    const closeH = source?.close_hour ?? f.close_hour;
    const newHours = Array.from({ length: 7 }, (_, day) => ({
      day, open_hour: openH, close_hour: closeH, is_closed: false,
    }));
    set("weekly_hours", newHours);
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newUrls = [];
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const r = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(r.data.public_id);
      }
      set("images", [...(f.images || []), ...newUrls]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Upload failed: ${typeof detail === "string" ? detail : "Unknown error"}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const enabledDurations = Object.entries(slotConfigs).filter(([, v]) => v.enabled).map(([k]) => parseInt(k)).sort((a, b) => a - b);
      const pricing = {};
      Object.entries(slotConfigs).forEach(([k, v]) => { pricing[k] = v.price; });
      const payload = {
        ...f,
        price_per_slot: pricing[String(defaultDuration)] ?? f.price_per_slot,
        slot_durations: enabledDurations,
        slot_pricing: pricing,
        default_slot_duration: defaultDuration,
        peak_multiplier: Math.round((f.peak_multiplier / 100 + 1) * 100) / 100,
        weekend_multiplier: Math.round((f.weekend_multiplier / 100 + 1) * 100) / 100,
      };
      if (isEditing) {
        await api.patch(`/courts/${courtId}`, payload);
        toast.success(t("common.court_updated"));
      } else {
        await api.post("/courts", payload);
        localStorage.removeItem(DRAFT_KEY);
        toast.success(t("common.court_created"));
      }
      navigate(-1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 0) return f.name.trim().length > 0;
    if (step === 1) return f.city.trim().length > 0;
    return true;
  };

  const goNext = () => {
    if (step === 0) setTouched((prev) => ({ ...prev, name: true }));
    if (step === 1) setTouched((prev) => ({ ...prev, city: true }));
    if (canNext()) setStep(step + 1);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }

  const handleSportChange = (e) => {
    const newSport = e.target.value;
    const validTypes = COURT_TYPES_BY_SPORT[newSport]?.map((t) => t.value) || [];
    const validSurfaces = SURFACES_BY_SPORT[newSport]?.map((s) => s.value) || [];
    setF((prev) => ({
      ...prev, sport: newSport,
      court_type: prev.court_type && !validTypes.includes(prev.court_type) ? "" : prev.court_type,
      court_surface: prev.court_surface && !validSurfaces.includes(prev.court_surface) ? "" : prev.court_surface,
    }));
  };

  const togglePaymentMethod = (key, checked) => {
    const cur = f.accepted_payment_methods || ["online"];
    set("accepted_payment_methods", checked ? [...cur, key] : cur.filter((x) => x !== key));
  };

  const errorInputClass = (field) => fieldError(field) ? "!border-red-400" : "";

  return (
    <>
      {/* Draft recovery modal */}
      {showDraftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border max-w-md w-full mx-4 p-6">
            <h3 className="font-heading text-xl mb-2">Saved Draft Found</h3>
            <p className="text-sm text-muted-foreground mb-6">
              You have an unsaved court draft. Would you like to continue where you left off or start fresh?
            </p>
            <div className="flex gap-3">
              <button onClick={loadDraft} className="btn-volt rounded-sm px-6 py-3 text-xs flex-1">
                Load Draft
              </button>
              <button onClick={clearDraft} className="btn-outline rounded-sm px-6 py-3 text-xs flex-1">
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground hover:text-volt transition-colors mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-2">
          {isEditing ? t("owner.court_form.edit_court") : t("owner.court_form.new_court")}
        </h1>
        <p className="text-sm text-muted-foreground/80 mb-6">{t(`owner.court_form.wizard_${STEPS[step].key}_desc`)}</p>

        {/* Progress bar */}
        <div className="hidden md:block h-0.5 bg-card mb-2 overflow-hidden">
          <div className="h-full bg-volt transition-all duration-300 ease-out" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <p className="hidden md:block text-[10px] text-muted-foreground/60 tracking-widest uppercase mb-8">
          {t("owner.court_form.progress", { current: step + 1, total: STEPS.length })}
        </p>

        <div className="md:grid md:grid-cols-[220px_1fr] gap-8">
          {/* Steps - horizontal on mobile, vertical on desktop */}
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-2 mb-4 md:mb-0 -mx-5 md:mx-0 px-5 md:px-0">
            {STEPS.map((s, i) => {
              const isActive = i === step;
              const isDone = i < step;
              const isClickable = isEditing || isDone || isActive;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => isClickable && setStep(i)}
                  className={`shrink-0 md:w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 text-xs tracking-widest uppercase font-bold transition-all ${
                    isActive
                      ? "bg-volt/10 text-volt border-b-2 md:border-b-0 md:border-s-2 border-volt"
                      : isEditing || isDone
                      ? "text-volt/70 hover:text-volt hover:bg-card cursor-pointer border-b-2 md:border-b-0 md:border-s-2 border-transparent md:border-volt/20"
                      : "text-muted-foreground/60 border-b-2 md:border-b-0 md:border-s-2 border-transparent cursor-default"
                  }`}
                >
                  {isEditing || isDone ? (
                    <Check className="h-4 w-4 shrink-0" />
                  ) : (
                    <span className="w-4 text-center shrink-0">{i + 1}</span>
                  )}
                  {t(s.labelKey)}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div>

        {step === 0 && (
          <section>
            <div className="border border-volt/20 bg-volt/[0.035] p-4 sm:p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 shrink-0 border border-volt/30 bg-volt/10 flex items-center justify-center text-volt">
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground">
                    {t("owner.court_form.maps_import_title")}
                  </h2>
                  <p id="maps-import-help" className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {t("owner.court_form.maps_import_help")}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  inputMode="url"
                  dir="ltr"
                  autoComplete="off"
                  aria-label={t("owner.court_form.maps_import_input_label")}
                  aria-describedby="maps-import-help"
                  value={mapsUrl}
                  onChange={(e) => {
                    setMapsUrl(e.target.value);
                    setMapsImportError("");
                    setMapsImportResult(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      importGoogleMapsPlace();
                    }
                  }}
                  placeholder={t("owner.court_form.maps_import_placeholder")}
                  className="min-w-0 flex-1 bg-card border border-border px-4 py-3 text-sm text-left focus:border-volt focus:outline-none"
                />
                <button
                  type="button"
                  onClick={importGoogleMapsPlace}
                  disabled={!mapsUrl.trim() || mapsImporting}
                  aria-busy={mapsImporting}
                  className="btn-volt rounded-sm min-h-[46px] px-5 py-3 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed sm:shrink-0"
                >
                  {mapsImporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MapPin className="h-4 w-4" />
                  )}
                  {mapsImporting
                    ? t("owner.court_form.maps_importing")
                    : t("owner.court_form.maps_import_button")}
                </button>
              </div>

              {mapsImportError && (
                <p role="alert" className="mt-3 text-xs text-red-400 leading-relaxed">
                  {mapsImportError}
                </p>
              )}

              {mapsImportResult && (
                <div role="status" className="mt-3 border border-emerald-500/25 bg-emerald-500/[0.06] p-3">
                  <div className="flex items-start gap-2 text-emerald-400">
                    <Check className="h-4 w-4 shrink-0 mt-0.5" />
                    <p className="text-xs leading-relaxed">
                      {t("owner.court_form.maps_import_success")}
                    </p>
                  </div>
                  {mapsImportResult.fields.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground me-1">
                        {t("owner.court_form.maps_import_fields")}
                      </span>
                      {mapsImportResult.fields.map((field) => (
                        <span key={field} className="border border-emerald-500/20 bg-background/50 px-2 py-1 text-[10px] text-foreground/80">
                          {t(`owner.court_form.maps_import_field_${field}`)}
                        </span>
                      ))}
                    </div>
                  )}
                  {mapsImportResult.keptExisting && (
                    <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                      {t("owner.court_form.maps_import_kept_existing")}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-6 mt-6">
              <InputField label={t("owner.court_form.name")} asterisk>
                <TextInput
                  dataTestid="court-form-name"
                  value={f.name}
                  onChange={(e) => { set("name", e.target.value); if (!touched.name) touch("name"); }}
                  onBlur={() => touch("name")}
                  placeholder={t("owner.court_form.name_placeholder")}
                  className={errorInputClass("name")}
                />
                {fieldError("name") && <p className="text-[10px] text-red-400 mt-1">{errorMsg("name")}</p>}
              </InputField>
              <div className="grid grid-cols-2 gap-6">
                <InputField label={t("owner.court_form.sport")} asterisk>
                  <SelectInput value={f.sport} onChange={handleSportChange}>
                    {SPORTS.map((s) => (
                      <option key={s} value={s} className="bg-background">{t("home.sports." + s)}</option>
                    ))}
                  </SelectInput>
                </InputField>
                <InputField label={t("owner.court_form.court_type")}>
                  <div className="flex items-center min-h-[44px]">
                    <SelectInput value={f.court_type || ''} onChange={(e) => set("court_type", e.target.value)}>
                      <option value="">—</option>
                      {(COURT_TYPES_BY_SPORT[f.sport] || []).map((ct) => (
                        <option key={ct.value} value={ct.value}>{t(ct.labelKey)}</option>
                      ))}
                    </SelectInput>
                  </div>
                </InputField>
              </div>
              <div className="grid grid-cols-2 gap-6">
              <InputField label={t("owner.court_form.court_surface")}>
                <div className="flex items-center min-h-[44px]">
                  <SelectInput value={f.court_surface || ''} onChange={(e) => set("court_surface", e.target.value)}>
                    <option value="">—</option>
                    {(SURFACES_BY_SPORT[f.sport] || []).map((s) => (
                      <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
                    ))}
                  </SelectInput>
                </div>
              </InputField>
              <InputField label={t("owner.court_form.setting")}>
                <div className="flex items-center min-h-[44px]">
                  <SelectInput value={f.indoor ? "indoor" : "outdoor"} onChange={(e) => set("indoor", e.target.value === "indoor")}>
                    <option value="indoor">{t("owner.court_form.indoor")}</option>
                    <option value="outdoor">{t("owner.court_form.outdoor")}</option>
                  </SelectInput>
                </div>
              </InputField>
            </div>
            </div>
            {facilities.length > 0 ? (
              <InputField label={t("owner.facility")} className="mt-6" asterisk>
                <SelectInput value={f.facility_id || ''} onChange={(e) => {
                  const fid = e.target.value;
                  set("facility_id", fid);
                  const fac = facilities.find((fa) => fa.id === fid);
                  if (fac) {
                    setF((prev) => ({
                      ...prev,
                      city: fac.city || prev.city,
                      address: fac.address || prev.address,
                      lat: fac.lat || prev.lat,
                      lng: fac.lng || prev.lng,
                    }));
                  }
                }}>
                  <option value="">{t("owner.facility_none")}</option>
                  {facilities.map((fac) => (
                    <option key={fac.id} value={fac.id} className="bg-background">{fac.name}</option>
                  ))}
                </SelectInput>
              </InputField>
            ) : (
              <div className="mt-6 p-4 border border-dashed border-border bg-card">
                <p className="text-[10px] text-muted-foreground/80 tracking-wider">{t("owner.facility_auto_created")}</p>
              </div>
            )}
            <InputField label={t("owner.court_form.description")} className="mt-6">
              <TextArea
                dataTestid="court-form-description"
                rows={4}
                value={f.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder={t("owner.court_form.description_placeholder")}
              />
            </InputField>
            <div className="mt-6">
              <label className="label-eyebrow block mb-2">{t("owner.court_form.images")}</label>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={(e) => uploadFiles(Array.from(e.target.files || []))} className="hidden" data-testid="court-form-file-input" />
              <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); uploadFiles(Array.from(e.dataTransfer.files || [])); }}
                onClick={() => !uploading && fileRef.current?.click()}
                className={`border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${uploading ? "opacity-60 cursor-wait" : dragging ? "border-volt bg-volt/5" : "border-border hover:border-foreground/25"}`}
              >
                {uploading ? (
                  <>
                    <div className="h-6 w-6 mx-auto mb-2 border-2 border-volt border-t-transparent animate-spin" />
                    <p className="text-xs text-volt">{t("owner.court_form.uploading")}</p>
                  </>
                ) : (
                  <>
                    <Upload className={`h-6 w-6 mx-auto mb-2 transition-colors ${dragging ? "text-volt" : "text-muted-foreground/60"}`} />
                    <p className="text-xs text-muted-foreground">{t("owner.court_form.drag_drop")}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{t("owner.court_form.drag_drop_hint")}</p>
                  </>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <input value={imgInput} onChange={(e) => setImgInput(e.target.value)} placeholder={t("owner.court_form.or_paste_url")} className="flex-1 bg-card border border-border px-4 py-3 focus:border-volt focus:outline-none" />
                <button type="button" onClick={() => { if (imgInput) { set("images", [...f.images, imgInput]); setImgInput(""); } }} className="btn-outline rounded-sm px-4 py-3 text-xs">{t("owner.court_form.add")}</button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-3">
                {f.images.map((u, i) => (
                  <div key={i} className="relative aspect-[4/3] border border-border">
                    <img src={getImageUrl(u, 'thumbnail')} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/80 p-1"><X className="h-3 w-3 text-volt" /></button>
                  </div>
                ))}
              </div>
            </div>
            <InputField label={t("owner.court_form.booking_mode")} className="mt-6">
              <SegmentedToggle
                options={[
                  { value: true, label: t("owner.court_form.auto_confirm") },
                  { value: false, label: t("owner.court_form.manual_confirm") },
                ]}
                value={f.auto_accept}
                onChange={(v) => set("auto_accept", v)}
              />
              <p className="text-[10px] text-muted-foreground/80 mt-2 tracking-wider">{t("owner.court_form.auto_accept_hint")}</p>
            </InputField>
            <NavButtons step={step} setStep={setStep} canNext={canNext} goNext={goNext} submitting={submitting} submit={submit} t={t} navigate={navigate} isEditing={isEditing} />
          </section>
        )}

        {step === 1 && (
          <section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <InputField label={t("owner.court_form.country")}>
                <SelectInput value={f.country || "JO"} onChange={(e) => { set("country", e.target.value); set("currency", "JOD"); }}>
                  <option value="JO" className="bg-background">Jordan</option>
                </SelectInput>
              </InputField>
              <InputField label={t("owner.court_form.city")} asterisk>
                <SelectInput value={f.city} onChange={(e) => { const c = e.target.value; touch("city"); setF((prev) => { const coords = CITY_COORDS[c]; return { ...prev, city: c, ...(coords ? { lat: coords.lat, lng: coords.lng } : {}) }; }); }} className={errorInputClass("city")}>
                  {f.city && !(CITIES_BY_COUNTRY[f.country] || CITIES_BY_COUNTRY.JO).includes(f.city) && (
                    <option value={f.city} className="bg-background">{f.city}</option>
                  )}
                  {(CITIES_BY_COUNTRY[f.country] || CITIES_BY_COUNTRY.JO).map((cityKey) => (
                    <option key={cityKey} value={cityKey} className="bg-background">{t("cities." + cityKey)}</option>
                  ))}
                </SelectInput>
                {fieldError("city") && <p className="text-[10px] text-red-400 mt-1">{errorMsg("city")}</p>}
              </InputField>
            </div>
            <InputField label={t("owner.court_form.address")} asterisk className="mt-6">
              <TextInput
                dataTestid="court-form-address"
                value={f.address}
                onChange={(e) => { set("address", e.target.value); if (!touched.address) touch("address"); }}
                onBlur={() => touch("address")}
                placeholder={t("owner.court_form.address_placeholder")}
                className={errorInputClass("address")}
              />
              {fieldError("address") && <p className="text-[10px] text-red-400 mt-1">{errorMsg("address")}</p>}
            </InputField>
            <InputField label={t("owner.court_form.location")} className="mt-6">
              <MapPicker lat={f.lat} lng={f.lng} onChange={(lat, lng) => setLatLng(lat, lng)} />
              <p className="text-xs text-muted-foreground/80 mt-2 tracking-wider">
                {f.lat.toFixed(5)}, {f.lng.toFixed(5)} &mdash; {t("owner.court_form.click_map")}
              </p>
            </InputField>

            <div className="border-t border-border my-8" />
            <h3 className="label-eyebrow mb-4">{t("owner.court_form.contact_heading")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField label={t("owner.court_form.whatsapp_number")}>
                <TextInput value={f.whatsapp_number || ''} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+9627XXXXXXXX" />
                <p className="text-[10px] text-muted-foreground/80 mt-1 tracking-wider">{t("owner.court_form.whatsapp_hint")}</p>
              </InputField>
              <InputField label={t("owner.court_form.phone_number")}>
                <TextInput value={f.phone_number || ''} onChange={(e) => set("phone_number", e.target.value)} placeholder="+9627XXXXXXXX" />
              </InputField>
            </div>
            <div className="mt-6">
              <label className="label-eyebrow block mb-2">{t("owner.court_form.contact_visibility")}</label>
              <SegmentedToggle
                options={[
                  { value: "confirmed_only", label: t("owner.court_form.contact_confirmed_only") },
                  { value: "always", label: t("owner.court_form.contact_always") },
                ]}
                value={f.contact_visibility}
                onChange={(v) => set("contact_visibility", v)}
              />
              <p className="text-[10px] text-muted-foreground/80 mt-2 tracking-wider">
                {f.contact_visibility === "confirmed_only" ? t("owner.court_form.contact_confirmed_only_desc") : t("owner.court_form.contact_always_desc")}
              </p>
            </div>

            <NavButtons step={step} setStep={setStep} canNext={canNext} goNext={goNext} submitting={submitting} submit={submit} t={t} navigate={navigate} isEditing={isEditing} />
          </section>
        )}

        {step === 2 && (
          <section>
            <div>
              <label className="label-eyebrow block mb-3">{t("owner.court_form.amenities")}</label>
              {["Essential", "Match", "Comfort", "Pro"].map((cat) => {
                const catItems = allAmenities.filter((a) => a.category === cat);
                if (catItems.length === 0) return null;
                return (
                  <div key={cat} className="mb-6">
                    <p className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-3">{t(`owner.court_form.amenity_cat_${cat}`)}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {catItems.map((a) => {
                        const selected = f.amenities.includes(a.id);
                        const displayName = i18n.language?.startsWith("ar") ? a.nameAr : a.nameEn;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => set("amenities", selected ? f.amenities.filter((k) => k !== a.id) : [...f.amenities, a.id])}
                            className={`flex items-center gap-2 px-3 py-2.5 text-xs border text-start transition-all ${
                              selected
                                ? "border-volt bg-volt/5 text-volt"
                                : "border-border bg-card text-foreground/70 hover:border-foreground/20"
                            }`}
                          >
                            <div className={`w-4 h-4 shrink-0 border flex items-center justify-center ${selected ? "bg-volt border-volt" : "border-muted-foreground/30"}`}>
                              {selected && <Check className="h-3 w-3 text-background" />}
                            </div>
                            <AmenityIcon name={a.icon} className="h-3.5 w-3.5" />
                            <span className="truncate">{displayName || a.id}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {f.amenities.length > 0 && (
                <p className="text-xs text-muted-foreground mt-3">{f.amenities.length} selected</p>
              )}
            </div>
            <div className="mt-8">
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">{t("owner.court_form.extras")}</label>
                <button type="button" onClick={() => setExtras([...(f.extras || []), { name: "", description: "", price: 0, optional: true, min_qty: 0, max_qty: 1 }])} className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1">
                  <Plus className="h-3 w-3" /> {t("owner.court_form.add_extras")}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground/80 mb-4 tracking-wider">{t("owner.court_form.extras_hint")}</p>
              <div className="space-y-3">
                {(f.extras || []).map((extra, i) => (
                  <ExtraItemEditor key={i} extra={extra} index={i} extras={f.extras} setExtras={setExtras} t={t} />
                ))}
              </div>
            </div>
            <NavButtons step={step} setStep={setStep} canNext={canNext} goNext={goNext} submitting={submitting} submit={submit} t={t} navigate={navigate} isEditing={isEditing} />
          </section>
        )}

        {step === 3 && (
          <section>
            <div>
              <label className="label-eyebrow block mb-3">{t("owner.court_form.slot_durations")}</label>
              <div className="border border-border divide-y divide-white/5">
                {[30, 60, 90, 120].map((dur) => {
                  const key = String(dur);
                  const cfg = slotConfigs[key];
                  const enabled = cfg?.enabled ?? true;
                  return (
                    <div key={dur} className={`flex items-center justify-between px-4 py-3 transition-colors ${enabled ? "bg-card" : "bg-background/50"}`}>
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <span className={`text-xs font-mono whitespace-nowrap w-14 flex-shrink-0 ${enabled ? "text-foreground" : "text-muted-foreground/60"}`}>{dur} {t("owner.court_form.min")}</span>
                        {enabled ? (
                          <div className="relative flex-1 max-w-[120px]">
                            <input
                              type="number" min={0} step={0.5}
                              value={cfg?.price ?? ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setSlotConfigs((prev) => ({ ...prev, [key]: { ...prev[key], price: parseFloat(e.target.value) || 0 } }))}
                              className="w-full bg-background border border-input px-3 py-1.5 pr-10 text-sm focus:border-volt focus:outline-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/80 tracking-widest uppercase font-semibold pointer-events-none">
                              {f.currency === "JOD" ? "JD" : f.currency}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground/40 tracking-wider">{t("owner.court_form.disabled")}</span>
                        )}
                      </div>
                      <Switch
                        checked={enabled}
                        onCheckedChange={() => {
                          setSlotConfigs((prev) => {
                            const next = { ...prev, [key]: { ...prev[key], enabled: !prev[key]?.enabled } };
                            const enabledKeys = Object.entries(next).filter(([, v]) => v.enabled).map(([k]) => parseInt(k));
                            if (!next[key].enabled && defaultDuration === parseInt(key)) {
                              setDefaultDuration(enabledKeys[0] || 60);
                            }
                            return next;
                          });
                        }}
                        className="data-[state=checked]:bg-volt data-[state=unchecked]:bg-border/30 [&>span]:bg-white flex-shrink-0"
                      />
                    </div>
                  );
                })}
              </div>
              {Object.values(slotConfigs).filter((v) => v.enabled).length > 1 && (
                <div className="mt-4">
                  <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-2 block">{t("owner.court_form.default_slot_duration")}</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(slotConfigs).filter(([, v]) => v.enabled).map(([dur]) => (
                      <button
                        key={dur}
                        type="button"
                        onClick={() => setDefaultDuration(parseInt(dur))}
                        className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-all ${
                          defaultDuration === parseInt(dur)
                            ? "bg-volt text-primary-foreground border-volt"
                            : "border-border text-muted-foreground hover:border-volt/50"
                        }`}
                      >
                        {dur}{t("owner.court_form.min_short")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6">
              <label className="label-eyebrow block mb-2">{t("owner.court_form.cancellation_policy")}</label>
              <div className="mb-3">
                <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.court_form.cancellation_preset")}</label>
                <div className="flex flex-wrap gap-2">
                  {["none", "flexible", "standard", "strict", "custom"].map((preset) => {
                    const tiersStr = JSON.stringify(f.cancellation_tiers);
                    const matchesPreset = (p) => JSON.stringify(CANCELLATION_PRESETS[p]) === tiersStr;
                    const isActive = preset === "custom"
                      ? (f.cancellation_tiers || []).length > 0 && !matchesPreset("none") && !matchesPreset("flexible") && !matchesPreset("standard") && !matchesPreset("strict")
                      : matchesPreset(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          if (preset === "custom") {
                            if (!(f.cancellation_tiers || []).length) set("cancellation_tiers", [{ hours_before: 48, refund_percent: 100 }]);
                          } else {
                            set("cancellation_tiers", [...CANCELLATION_PRESETS[preset]]);
                          }
                        }}
                        className={`px-4 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                          isActive
                            ? "bg-volt text-primary-foreground border-volt"
                            : "border-border text-muted-foreground hover:border-foreground/30"
                        }`}
                      >
                        {t(`owner.court_form.cancellation_preset_${preset}`)}
                      </button>
                    );
                  })}
                </div>
                {(["none", "flexible", "standard", "strict"].includes(
                  Object.entries(CANCELLATION_PRESETS).find(([, v]) => JSON.stringify(v) === JSON.stringify(f.cancellation_tiers))?.[0] || ""
                )) && (
                  <p className="text-[10px] text-volt/60 mt-1.5 tracking-wider">
                    {t(`owner.court_form.cancellation_preset_${Object.entries(CANCELLATION_PRESETS).find(([, v]) => JSON.stringify(v) === JSON.stringify(f.cancellation_tiers))?.[0]}_desc`)}
                  </p>
                )}
              </div>

              {(f.cancellation_tiers || []).length > 0 && (
                <>
                  <div className="border border-border divide-y divide-white/5">
                    {[...(f.cancellation_tiers || [])].sort((a, b) => b.hours_before - a.hours_before).map((tier, sortedIdx) => {
                      const realIdx = (f.cancellation_tiers || []).findIndex((t) => t === tier);
                      return (
                        <div key={sortedIdx} className="flex items-center gap-4 px-4 py-3 bg-card">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="relative max-w-[90px]">
                              <input
                                type="number" min={0} max={720} step={1}
                                value={tier.hours_before}
                                onChange={(e) => {
                                  const next = [...(f.cancellation_tiers || [])];
                                  next[realIdx] = { ...next[realIdx], hours_before: parseInt(e.target.value) || 0 };
                                  set("cancellation_tiers", next);
                                }}
                                className="w-full bg-background border border-input px-3 py-1.5 pr-10 text-sm focus:border-volt focus:outline-none"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground/80 tracking-wider uppercase pointer-events-none">{t("owner.court_form.cancellation_hours_label")}</span>
                            </div>
                            <span className="text-[10px] text-muted-foreground/60">→</span>
                            <div className="relative max-w-[90px]">
                              <input
                                type="number" min={0} max={100} step={5}
                                value={tier.refund_percent}
                                onChange={(e) => {
                                  const next = [...(f.cancellation_tiers || [])];
                                  next[realIdx] = { ...next[realIdx], refund_percent: parseInt(e.target.value) || 0 };
                                  set("cancellation_tiers", next);
                                }}
                                className="w-full bg-background border border-input px-3 py-1.5 pr-8 text-sm focus:border-volt focus:outline-none"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground/80 pointer-events-none">%</span>
                            </div>
                            <span className="text-[10px] text-muted-foreground/80 tracking-wider">
                              {tier.refund_percent === 100 ? t("owner.court_form.cancellation_free") : `${tier.refund_percent}% ${t("owner.court_form.cancellation_fee")}`}
                            </span>
                          </div>
                          {(f.cancellation_tiers || []).length > 1 && (
                            <button type="button" onClick={() => set("cancellation_tiers", (f.cancellation_tiers || []).filter((_, j) => j !== realIdx))} className="text-muted-foreground/60 hover:text-red-400 transition-colors">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <button type="button" onClick={() => set("cancellation_tiers", [...(f.cancellation_tiers || []), { hours_before: 0, refund_percent: 0 }])} className="text-[10px] text-volt/70 hover:text-volt tracking-widest uppercase font-bold transition-colors flex items-center gap-1">
                      <Plus className="h-3 w-3" /> {t("owner.court_form.cancellation_tier_add")}
                    </button>
                    <span className="text-[10px] text-muted-foreground/60">{t("owner.court_form.cancellation_tier_hint")}</span>
                  </div>
                </>
              )}
              {!(f.cancellation_tiers || []).length && (
                <p className="text-[10px] text-muted-foreground/60 mt-2 tracking-wider">{t("owner.court_form.cancellation_tier_hint")}</p>
              )}
            </div>

            {/* Weather + No-show */}
            <div className="mt-6 border border-border/30 bg-card p-6">
              <div className="label-eyebrow mb-4">{t("owner.court_form.cancellation_preview")}</div>
              <div className="space-y-3">
                <PaymentMethodRow
                  label={t("owner.court_form.weather_cancellation")}
                  description={t("owner.court_form.weather_cancellation_desc")}
                  checked={f.weather_cancellation}
                  onCheckedChange={(v) => set("weather_cancellation", v)}
                />
                <PaymentMethodRow
                  label={t("owner.court_form.no_show_policy")}
                  description={t("owner.court_form.no_show_policy_desc")}
                  checked={f.no_show_keep_deposit}
                  onCheckedChange={(v) => set("no_show_keep_deposit", v)}
                />
              </div>
            </div>
            <div className="mt-6">
              <label className="label-eyebrow block mb-3">{t("owner.court_form.payment_methods")}</label>
              <div className="border border-border divide-y divide-white/5">
                {/* Cliq */}
                <div>
                  <PaymentMethodRow
                    label={t("owner.cliQ_enabled")}
                    description={t("owner.cliQ_toggle_hint")}
                    checked={!!f.cliQ_enabled}
                    onCheckedChange={(checked) => setF({ ...f, cliQ_enabled: checked })}
                  />
                  {f.cliQ_enabled && (
                    <div className="bg-background/80 px-4 py-4 border-t border-border/30">
                      <InputField label={t("owner.cliQ_alias")}>
                        <TextInput
                          value={f.cliQ_alias || ''}
                          onChange={(e) => setF({ ...f, cliQ_alias: e.target.value })}
                          placeholder="Alias or phone number"
                          dir="ltr"
                        />
                      </InputField>
                    </div>
                  )}
                </div>

                {PAYMENT_METHODS.map((pm) => {
                  if (pm.key === "cod") {
                    const codEnabled = (f.accepted_payment_methods || ["online"]).includes("cod");
                    return (
                      <div key="cod">
                        <PaymentMethodRow
                          label={t(pm.labelKey)}
                          description={pm.desc}
                          checked={codEnabled}
                          onCheckedChange={(checked) => togglePaymentMethod("cod", checked)}
                        />
                        {codEnabled && (
                          <div className="bg-background/80 px-4 py-4 border-t border-border/30">
                            <div className="flex items-center gap-3">
                              <div className="relative max-w-[120px]">
                                <input
                                  type="number"
                                  min={0}
                                  max={50}
                                  step={5}
                                  value={f.deposit_percent ?? 0}
                                  onChange={(e) => set("deposit_percent", parseInt(e.target.value) || 0)}
                                  className="w-full bg-background border border-input px-4 py-3 pr-10 focus:border-volt focus:outline-none"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground/80 pointer-events-none">%</span>
                              </div>
                              <span className="text-[10px] text-muted-foreground/80 tracking-wider">{t("owner.court_form.deposit_hint")}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <PaymentMethodRow
                      key={pm.key}
                      label={t(pm.labelKey)}
                      description={pm.desc}
                      checked={(f.accepted_payment_methods || ["online"]).includes(pm.key)}
                      onCheckedChange={(checked) => togglePaymentMethod(pm.key, checked)}
                    />
                  );
                })}
              </div>
            </div>
            <div className="mt-6 border border-border/30 bg-card p-6">
              <div className="label-eyebrow flex items-center gap-2 mb-4">
                <Flame className="h-3.5 w-3.5 text-volt" /> {t("owner.court_form.dynamic_pricing")}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.court_form.peak_multiplier")}</label>
                  <div className="relative">
                    <input type="number" step="5" min="0" max="200" data-testid="court-form-peak-mult"
                      value={f.peak_multiplier} onChange={(e) => set("peak_multiplier", parseFloat(e.target.value) || 0)}
                      className="w-full bg-background border border-input p-3 pr-10 focus:border-volt focus:outline-none" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/80 tracking-widest uppercase pointer-events-none">%</span>
                  </div>
                  {f.peak_multiplier > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {Object.entries(slotConfigs).filter(([, v]) => v.enabled).map(([dur, v]) => {
                        const base = v.price;
                        const mult = 1 + f.peak_multiplier / 100;
                        const newPrice = Math.round(base * mult * 100) / 100;
                        const currency = f.currency === "JOD" ? "JD" : f.currency;
                        return (
                          <div key={dur} className="text-[10px] text-volt/70 tracking-wider">
                            {dur}min {currency}{base} <span className="text-muted-foreground/60">→</span> {currency}{newPrice}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.court_form.weekend_multiplier")}</label>
                  <div className="relative">
                    <input type="number" step="5" min="0" max="200" data-testid="court-form-weekend-mult"
                      value={f.weekend_multiplier} onChange={(e) => set("weekend_multiplier", parseFloat(e.target.value) || 0)}
                      className="w-full bg-background border border-input p-3 pr-10 focus:border-volt focus:outline-none" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/80 tracking-widest uppercase pointer-events-none">%</span>
                  </div>
                  {f.weekend_multiplier > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {Object.entries(slotConfigs).filter(([, v]) => v.enabled).map(([dur, v]) => {
                        const base = v.price;
                        const mult = 1 + f.weekend_multiplier / 100;
                        const newPrice = Math.round(base * mult * 100) / 100;
                        const currency = f.currency === "JOD" ? "JD" : f.currency;
                        return (
                          <div key={dur} className="text-[10px] text-volt/70 tracking-wider">
                            {dur}min {currency}{base} <span className="text-muted-foreground/60">→</span> {currency}{newPrice}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6">
                <div className="label-eyebrow flex items-center gap-2 mb-4">
                  <Flame className="h-3.5 w-3.5 text-volt" /> {t("owner.court_form.peak_hours")}
                </div>
                <PeakHourGrid peakHours={f.peak_hours} togglePeak={togglePeak} />
              </div>
            </div>
            <NavButtons step={step} setStep={setStep} canNext={canNext} goNext={goNext} submitting={submitting} submit={submit} t={t} navigate={navigate} isEditing={isEditing} />
          </section>
        )}

        {step === 4 && (
          <section>
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">{t("owner.court_form.weekly_schedule")}</label>
                <button type="button" onClick={applyToAllDays} className="text-[10px] text-volt/70 hover:text-volt tracking-widest uppercase font-bold transition-colors">
                  {t("owner.court_form.apply_to_all_days")}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground/80 mb-4 tracking-wider">{t("owner.court_form.weekly_schedule_hint")}</p>
              <div className="space-y-2">
                {Array.from({ length: 7 }).map((_, day) => (
                  <WeeklyDayRow
                    key={day}
                    dayKey={t(`calendar.${DAY_KEYS[day]}`)}
                    day={day}
                    entry={(f.weekly_hours || []).find((wh) => wh.day === day)}
                    defaultOpen={f.open_hour}
                    defaultClose={f.close_hour}
                    onChange={updateWeeklyDay}
                  />
                ))}
              </div>
            </div>
            <NavButtons step={step} setStep={setStep} canNext={canNext} goNext={goNext} submitting={submitting} submit={submit} t={t} navigate={navigate} isEditing={isEditing} />
          </section>
        )}

        {step === 5 && (
          <section>
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-volt">{t("owner.court_form.review_basic")}</span>
                  <button type="button" onClick={() => setStep(0)} className="text-xs text-muted-foreground/80 hover:text-volt underline">{t("owner.court_form.review_edit")}</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <ReviewRow label={t("owner.court_form.name")} value={f.name} />
                  <ReviewRow label={t("owner.court_form.sport")} value={t("home.sports." + f.sport)} />
                  {f.court_type && <ReviewRow label={t("owner.court_form.court_type")} value={t(COURT_TYPES_BY_SPORT[f.sport]?.find((ct) => ct.value === f.court_type)?.labelKey || f.court_type)} />}
                  {f.court_surface && <ReviewRow label={t("owner.court_form.court_surface")} value={t(SURFACES_BY_SPORT[f.sport]?.find((s) => s.value === f.court_surface)?.labelKey || f.court_surface)} />}
                  <ReviewRow label={t("owner.court_form.setting")} value={f.indoor ? t("owner.court_form.indoor") : t("owner.court_form.outdoor")} />
                  <ReviewRow label={t("owner.court_form.booking_mode")} value={f.auto_accept ? t("owner.court_form.auto_confirm") : t("owner.court_form.manual_confirm")} />
                  {f.facility_id && <ReviewRow label={t("owner.facility")} value={facilities.find((fac) => fac.id === f.facility_id)?.name || f.facility_id} />}
                  {f.description && <div className="sm:col-span-2"><ReviewRow label={t("owner.court_form.description")} value={f.description} /></div>}
                </div>
              </div>

              {/* Location & Contact */}
              <div className="border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-volt">{t("owner.court_form.review_location")}</span>
                  <button type="button" onClick={() => setStep(1)} className="text-xs text-muted-foreground/80 hover:text-volt underline">{t("owner.court_form.review_edit")}</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <ReviewRow label={t("owner.court_form.city")} value={f.city ? (CITIES_BY_COUNTRY.JO.includes(f.city) ? t("cities." + f.city) : f.city) : ""} />
                  <ReviewRow label={t("owner.court_form.address")} value={f.address} />
                  {f.whatsapp_number && <ReviewRow label={t("owner.court_form.whatsapp_number")} value={f.whatsapp_number} />}
                  {f.phone_number && <ReviewRow label={t("owner.court_form.phone_number")} value={f.phone_number} />}
                  <ReviewRow label={t("owner.court_form.contact_visibility")} value={f.contact_visibility === "confirmed_only" ? t("owner.court_form.contact_confirmed_only") : t("owner.court_form.contact_always")} />
                </div>
              </div>

              {/* Media & Extras */}
              <div className="border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-volt">{t("owner.court_form.review_media")}</span>
                  <button type="button" onClick={() => setStep(2)} className="text-xs text-muted-foreground/80 hover:text-volt underline">{t("owner.court_form.review_edit")}</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <ReviewRow label={t("owner.court_form.amenities")} value={f.amenities.length > 0 ? `${f.amenities.length} selected` : "—"} />
                  <ReviewRow label={t("owner.court_form.images")} value={f.images.length > 0 ? `${f.images.length} uploaded` : "—"} />
                  <ReviewRow label={t("owner.court_form.extras")} value={(f.extras || []).length > 0 ? `${f.extras.length} extras` : "—"} />
                </div>
              </div>

              {/* Pricing */}
              <div className="border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-volt">{t("owner.court_form.review_pricing")}</span>
                  <button type="button" onClick={() => setStep(3)} className="text-xs text-muted-foreground/80 hover:text-volt underline">{t("owner.court_form.review_edit")}</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <ReviewRow label={t("owner.court_form.slot_durations")} value={Object.entries(slotConfigs).filter(([, v]) => v.enabled).map(([k, v]) => `${k}${t("owner.court_form.min_short")} ${f.currency === "JOD" ? "JD" : f.currency}${v.price}`).join(", ") || "—"} />
                  <ReviewRow label={t("owner.court_form.deposit")} value={f.deposit_percent > 0 ? `${f.deposit_percent}%` : "—"} />
                  <ReviewRow label={t("owner.court_form.cancellation_policy")} value={(f.cancellation_tiers || []).length > 0 ? (f.cancellation_tiers || []).sort((a, b) => b.hours_before - a.hours_before).map((tier) => `${tier.hours_before}h → ${tier.refund_percent}%`).join(", ") : t("owner.court_form.cancellation_no_refund")} />
                  {f.weather_cancellation && <ReviewRow label={t("owner.court_form.weather_cancellation")} value="✓" />}
                  {f.no_show_keep_deposit && <ReviewRow label={t("owner.court_form.no_show_policy")} value="✓" />}
                  <ReviewRow label={t("owner.court_form.payment_methods")} value={(f.accepted_payment_methods || []).join(", ")} />
                  {f.peak_multiplier > 0 && <ReviewRow label={t("owner.court_form.peak_multiplier")} value={`+${f.peak_multiplier}%`} />}
                  {f.weekend_multiplier > 0 && <ReviewRow label={t("owner.court_form.weekend_multiplier")} value={`+${f.weekend_multiplier}%`} />}
                  {f.peak_hours?.length > 0 && (
                    <ReviewRow label={t("owner.court_form.peak_hours")} value={f.peak_hours.map((h) => String(h).padStart(2, "0")).join(", ")} />
                  )}
                </div>
              </div>

              {/* Scheduling */}
              <div className="border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-volt">{t("owner.court_form.review_scheduling")}</span>
                  <button type="button" onClick={() => setStep(4)} className="text-xs text-muted-foreground/80 hover:text-volt underline">{t("owner.court_form.review_edit")}</button>
                </div>
                <div className="text-sm">
                  {(f.weekly_hours || []).length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {(f.weekly_hours || []).map((wh) => (
                        <div key={wh.day} className="text-[11px]">
                          <span className="text-muted-foreground/80 uppercase tracking-wider">{t(`calendar.${DAY_KEYS[wh.day]}`)}: </span>
                          {wh.is_closed ? <span className="text-red-400">Closed</span> : <span className="text-foreground/80">{String(wh.open_hour).padStart(2, "0")}:00–{String(wh.close_hour).padStart(2, "0")}:00</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground/80">{t("owner.court_form.weekly_schedule_hint")}</span>
                  )}
                </div>
              </div>

              {/* Rules */}
              <InputField label={t("owner.court_form.rules")}>
                <TextArea rows={3} value={f.rules} onChange={(e) => set("rules", e.target.value)} placeholder={t("owner.court_form.rules_placeholder")} />
              </InputField>
            </div>
            <NavButtons step={step} setStep={setStep} canNext={canNext} goNext={goNext} submitting={submitting} submit={submit} t={t} navigate={navigate} showNext={false} isEditing={isEditing} />
          </section>
        )}
      </div>
          </div>
        </div>
    </div>
    </>
  );
}

function NavButtons({ step, setStep, canNext, goNext, submitting, submit, t, navigate, showNext = true, isEditing }) {
  return (
    <div className="flex items-center justify-between gap-4 mt-10 pt-6 border-t border-border sticky bottom-0 bg-background z-10 pb-6">
      <button type="button" onClick={() => step === 0 ? navigate(-1) : setStep(step - 1)} className="btn-outline rounded-sm px-6 py-3 text-xs flex items-center gap-2">
        <ChevronLeft className="h-3.5 w-3.5" />
        {step === 0 ? t("common.cancel") : t("common.back")}
      </button>
      {isEditing ? (
        <button type="button" disabled={submitting} onClick={submit} className="btn-volt rounded-sm px-8 py-3 text-xs">
          {submitting ? t("owner.court_form.saving") : t("owner.court_form.save_court")}
        </button>
      ) : showNext ? (
        <button type="button" onClick={goNext} disabled={!canNext()} className="btn-volt rounded-sm px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-40">
          {t("common.next")} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      ) : (
        <button type="button" disabled={submitting} onClick={submit} className="btn-volt rounded-sm px-8 py-3 text-xs">
          {submitting ? t("owner.court_form.saving") : t("owner.court_form.save_court")}
        </button>
      )}
    </div>
  );
}
